import { SettingsApplicationSelectAll, SettingsChangePriorityValue, SettingsAnswerEmptyValue } from 'Constants/Settings'
import { TranslationKeys } from 'Constants/TranslationKeys'
import { renderTranslation } from 'Helpers/GenericHelpers'
import {
  createAdditionalOptions,
  createOption,
  emptyRespondingEntities,
  filterDistinctValues,
  getAlreadyRespondedValues,
  getLastEditInfo,
  getPrioritySelectOptions,
} from 'Helpers/SettingsHelper'
import { sendAnswers } from 'Services/Settings/SettingsService'
import { AnswerProp, PossibleValues, Question } from 'Slices/Settings/SettingsSlice'
import { Col, Modal, Row, Select, Spin, TreeSelect } from 'antd'
import { useAppDispatch, useAppSelector } from 'hooks'
import { useEffect, useMemo, useState } from 'react'
interface StandardTreeSelectProps {
  storedValue: AnswerProp[]
  storeValue: Function
  userCanEdit: boolean
  possibleValues: PossibleValues
  question: Question
  respondingEntityId: string
  maxTagCount?: number | 'responsive'
}

function StandardTreeSelect(props: StandardTreeSelectProps) {
  const dispatch = useAppDispatch()
  const language = useAppSelector(state => state.present.language.value)
  const editedAnswers = useAppSelector(state => state.present.settings.editedAnswers)
  const deletedAnswers = useAppSelector(state => state.present.settings.deletedAnswers)

  const [filteredOptions, setFilteredOptions] = useState([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [virtualData, setVirtualData] = useState([])
  const [isLoading, setIsLoading] = useState(false)

  const enableChangePrio = props.storedValue.length > 0 && Object.keys(editedAnswers).length === 0
  const enableEmpty = props.storedValue.length > 0

  const options = Object.entries(props.possibleValues).map(([key, value]) => {
    const storedprio = props.storedValue?.find(stored => stored.value === key)?.priority
    return createOption(value.value, key, key, storedprio || 0)
  })

  const fullOptions = [
    ...options,
    ...createAdditionalOptions(language, enableChangePrio, enableEmpty, props.question.code),
  ]

  useEffect(() => {
    const respondedValues = getAlreadyRespondedValues(props.question, editedAnswers[props.question.settingsName])

    let optionsToFilter = fullOptions

    if (props.question.onlyDistinctValues) {
      optionsToFilter = filterDistinctValues(optionsToFilter, respondedValues)
      if (editedAnswers[props.question.settingsName]) {
        optionsToFilter = filterDistinctValues(optionsToFilter, editedAnswers[props.question.settingsName])
      }
    }
    setVirtualData(data)
    setFilteredOptions(optionsToFilter)
  }, [editedAnswers, props.storedValue, props.question, language, deletedAnswers])

  const handleSelectedValues = values => {
    if (values.some(item => item.value === SettingsChangePriorityValue)) {
      values = values.filter(x => x.value !== SettingsChangePriorityValue)
    } else {
      const editInfo = getLastEditInfo(data, values, props.question, props.respondingEntityId)
      if (values.some(item => item.value === SettingsApplicationSelectAll)) {
        //if 'all' value is selected every option is going to be stored except 'empty'

        let prio = 0
        props.storeValue(
          filteredOptions
            .filter(
              x =>
                x.value !== SettingsApplicationSelectAll &&
                x.value !== SettingsAnswerEmptyValue &&
                x.value !== SettingsChangePriorityValue,
            )
            .map(x => ({ value: x.value, priority: (prio += 1) } as AnswerProp)),
        )
        if (props.question.onlyDistinctValues) {
          emptyRespondingEntities(
            props.question,
            editedAnswers[props.question.settingsName],
            props.respondingEntityId,
            dispatch,
          )
        }
      } else if (values.some(item => item.value === SettingsAnswerEmptyValue)) {
        //If empty value was already among values and another value is selected, we remove 'empty' and add the new value

        if (values[0].value === SettingsAnswerEmptyValue) {
          let prio = 0

          props.storeValue(
            values
              .filter(item => item.value !== SettingsAnswerEmptyValue)
              .map(x => ({ value: x.value, priority: (prio += 1) } as AnswerProp)),
            editInfo,
          )
        } else {
          //else an empty array is going to be stored if 'empty' value is selected
          props.storeValue([], editInfo)
        }
      } else {
        let prio = 0
        props.storeValue(
          values.map(x => ({ value: x.value, priority: (prio += 1) } as AnswerProp)),
          editInfo,
        )
      }
    }
  }

  const data = fullOptions
    .filter(x => props.storedValue?.find(stored => stored.value === x.value))
    .sort((a, b) => {
      if (a.priority === 0 && b.priority !== 0) {
        return 1
      } else if (a.priority !== 0 && b.priority === 0) {
        return -1
      } else {
        return a.priority - b.priority
      }
    })

  const showModal = () => {
    setIsModalOpen(true)
    setIsLoading(true)
    setTimeout(() => {
      setIsLoading(false)
    }, 400)
  }

  const handleOk = () => {
    setIsModalOpen(false)
    handleSelectedValues(virtualData)
    dispatch(sendAnswers())
  }

  const handleCancel = () => {
    setIsModalOpen(false)
  }

  const handleChangePriority = (newPrio, oldPrio) => {
    setIsLoading(true)
    const copyData = [...virtualData]

    const indexToExchange = copyData.findIndex(obj => obj.priority === newPrio)
    const indextoUpdate = copyData.findIndex(obj => obj.priority === oldPrio)

    copyData[indextoUpdate].priority = newPrio
    copyData[indexToExchange].priority = oldPrio
    copyData.sort((a, b) => a.priority - b.priority)
    setVirtualData(copyData)
    setTimeout(() => {
      setIsLoading(false)
    }, 200)
  }

  const prioList = useMemo(() => {
    return (
      <Spin spinning={isLoading} size='large'>
        {virtualData.map(item => {
          return (
            <Row justify='space-between'>
              <Col>{item.label}</Col>
              <Col>
                <Select
                  value={item.priority}
                  style={{ width: 50 }}
                  onChange={newPrio => {
                    handleChangePriority(newPrio, item.priority)
                  }}
                  options={getPrioritySelectOptions(item, virtualData)}
                />
              </Col>
            </Row>
          )
        })}
      </Spin>
    )
  }, [virtualData, isLoading])

  return (
    <>
      <TreeSelect
        className='tree-select-settings'
        style={{ width: 100 }}
        value={data}
        maxTagCount={props.maxTagCount}
        onChange={values => {
          handleSelectedValues(values)
        }}
        onSelect={(value, node) => {
          if (node.className === 'priority-node') {
            showModal()
          }
        }}
        treeData={filteredOptions.sort((a, b) => {
          if (a.priority === 0 && b.priority !== 0) {
            return 1
          } else if (a.priority !== 0 && b.priority === 0) {
            return -1
          } else {
            return a.priority - b.priority
          }
        })}
        treeCheckable={true}
        treeCheckStrictly={true}
        getPopupContainer={triggerNode => {
          return document.getElementById('page-content-settings')
        }}
      />
      <Modal
        title={renderTranslation(language, TranslationKeys.changePriorityModalTitle)}
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        width={300}
        cancelText={renderTranslation(language, TranslationKeys.changePriorityCancel)}
        okText={renderTranslation(language, TranslationKeys.changePrioritySave)}
        className='priority-modal'
      >
        {prioList}
      </Modal>
    </>
  )
}

export default StandardTreeSelect
